import {
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon
} from "@mui/icons-material";
import {
  AppBar,
  Avatar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { type ReactNode, useState } from "react";

// import SearchInput from "../../components/SearchInput";
import enlightenLogo from "../../assets/enlighten.png";
import { SearchProvider } from "../../common/context/SearchContext";
import { PRODUCT_NAME } from "../../utils/terminology";
import { useAuth } from "../auth/AuthContext";
import { DRAWER_WIDTH, useLayout } from "./LayoutContext";
import { Navigation } from "./Navigation";

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  minWidth: 0,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  marginLeft: `-${DRAWER_WIDTH}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    marginLeft: 0
  })
}));

interface AppLayoutProps {
  children: ReactNode;
}

export const AppLayout = ({ children }: AppLayoutProps) => {
  const { user, signOut, tenantName } = useAuth();
  const { isDrawerOpen, setIsDrawerOpen } = useLayout();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleDrawerOpen = () => setIsDrawerOpen(true);
  const handleDrawerClose = () => setIsDrawerOpen(false);
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);

  const handleSignOut = async () => {
    await signOut();
    handleMenuClose();
  };

  return (
    <SearchProvider>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{ mr: 2, ...(isDrawerOpen && { display: "none" }) }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
              <img
                src={enlightenLogo}
                alt={`${PRODUCT_NAME} Logo`}
                style={{ height: "32px" }}
              />
            </Box>
            {/* <Box sx={{ mr: 2 }}>
              <SearchInput />
            </Box> */}
            <IconButton
              onClick={handleMenuOpen}
              size="small"
              sx={{ ml: 2 }}
              aria-controls={anchorEl ? "account-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={anchorEl ? "true" : undefined}
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.email?.[0].toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              id="account-menu"
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              onClick={handleMenuClose}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <MenuItem disabled sx={{ opacity: 0.7 }}>
                {user?.email}
              </MenuItem>
              <MenuItem
                component="a"
                href="/reset-password"
                onClick={handleSignOut}
              >
                Reset Password
              </MenuItem>
              <MenuItem onClick={handleSignOut}>Sign out</MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        <Drawer
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box"
            }
          }}
          variant="persistent"
          anchor="left"
          open={isDrawerOpen}
        >
          <Toolbar />
          <Box sx={{
            overflow: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 16px",
            backgroundColor: "grey.A200"
          }}>
            <Typography variant="h6" noWrap>
              {tenantName || "My Tenant"}
            </Typography>
          </Box>
          <Navigation />
          <IconButton
            onClick={handleDrawerClose}
            sx={{
              position: "fixed",
              left: 227,
              top: 85,
              backgroundColor: "primary.main",
              color: "white",
              // border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: "50%",
              width: 24,
              height: 24,
              padding: 0.5,
              // "&:hover": {
              //   backgroundColor: "action.hover",
              // },
              zIndex: (theme) => theme.zIndex.drawer + 1,
              boxShadow: 1
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Drawer>
        <Main open={isDrawerOpen}>
          <Toolbar />
          {children}
        </Main>
      </Box>
    </SearchProvider>
  );
};
